import React, { useEffect, useState } from 'react';
import { SalesOrder, CreateSalesOrderRequest } from '../../types/sales.types';
import SalesOrderForm from '../forms/SalesOrderForm';
import { Modal } from '../ui/Modal';
import { Field, inputClass, inputErrorClass } from '../ui/FormControls';
import { cn } from '../../lib/utils';

interface EditQuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateSalesOrderRequest) => Promise<void>;
  quote?: SalesOrder;
  isLoading?: boolean;
}

const EditQuoteModal: React.FC<EditQuoteModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  quote,
  isLoading = false,
}) => {
  const [validUntil, setValidUntil] = useState('');
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState('');
  useEffect(() => {
    setValidUntil(quote?.quoteValidUntil ? String(quote.quoteValidUntil).slice(0, 10) : '');
    setReason('');
    setReasonError('');
  }, [quote?.id, quote?.quoteRevision, quote?.quoteValidUntil, isOpen]);

  if (!quote) return null;
  // A quotation the customer already holds is revised, not overwritten
  const sent = !!quote.quoteSentAt;

  const handleSubmit = async (data: CreateSalesOrderRequest) => {
    if (sent && reason.trim().length < 3) {
      setReasonError('Say why the quotation is being revised');
      document.getElementById('quote-revision-reason')?.focus();
      return;
    }
    await onSubmit({ ...data, quoteValidUntil: validUntil || undefined, ...(sent ? { revisionReason: reason.trim() } : {}) });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !isLoading && onClose()}
      title={sent ? 'Revise Quote' : 'Edit Quote'}
      description={quote.quoteRevision ? `${quote.orderNumber} Rev ${quote.quoteRevision}` : quote.orderNumber}
      size="2xl"
      className="overflow-visible"
    >
      {sent && (
        <p className="mb-4 rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-900 ring-1 ring-amber-200">
          This quotation has been sent to the customer. Saving makes revision {(quote.quoteRevision ?? 0) + 1} and keeps the version that was sent.
        </p>
      )}
      <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Field label="Prices valid until" htmlFor="quote-valid-until" hint="Printed on the quotation">
          <input id="quote-valid-until" type="date" value={validUntil} onChange={(event) => setValidUntil(event.target.value)} className={inputClass} />
        </Field>
        {sent && (
          <Field label="Why is it revised?" htmlFor="quote-revision-reason" required error={reasonError} className="sm:col-span-2">
            <input
              id="quote-revision-reason"
              type="text"
              maxLength={300}
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
                setReasonError('');
              }}
              className={cn(inputClass, reasonError && inputErrorClass)}
              placeholder="Customer asked for a larger quantity"
            />
          </Field>
        )}
      </div>
      <SalesOrderForm
        key={`${quote.id}-${quote.quoteRevision ?? 0}`}
        onSubmit={handleSubmit}
        onCancel={onClose}
        isLoading={isLoading}
        initialData={quote}
        submitButtonText={sent ? 'Save as New Revision' : 'Update Quote'}
        mode="edit"
        checkStock={false}
      />
    </Modal>
  );
};

export default EditQuoteModal;
